// Team Chats: venue groups and direct messages between the team (not the AI).
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/src/lib/supabase';

export interface TeamChannel {
  id: string; kind: 'group' | 'dm'; name: string | null; system_key: string | null; dm_key: string | null;
  last_message_at: string | null; created_at: string;
}
export interface TeamPerson { id: string; name: string; role: string; position: string | null }
export interface TeamMessage {
  id: string; channel_id: string; user_id: string | null; body: string; image_path: string | null; mentions: string[];
  pinned_at: string | null; edited_at: string | null; deleted_at: string | null; created_at: string;
}
export const MSG_COLS = 'id, channel_id, user_id, body, image_path, mentions, pinned_at, edited_at, deleted_at, created_at';

export const teamDbMsg = (m: string) => /team_|ensure_team_chats|open_team_dm|schema cache/.test(m) && /does not exist|schema cache|Could not find/.test(m)
  ? `Team Chats need the latest database update (20261028100000_team_chats.sql). (${m})` : m;

export const SYSTEM_ORDER: Record<string, number> = { everyone: 0, foh: 1, kitchen: 2, managers: 3 };

/** Name to show for a chat: the group name, or the other person in a DM. */
export function channelTitle(ch: TeamChannel, me: string, people: Map<string, TeamPerson>): string {
  if (ch.kind === 'group') return ch.name || 'Group';
  const other = (ch.dm_key ?? '').split(':').find((x) => x !== me) ?? '';
  return people.get(other)?.name ?? 'Former team member';
}
export const dmOther = (ch: TeamChannel, me: string) => (ch.dm_key ?? '').split(':').find((x) => x !== me) ?? null;

export const initials = (n: string) => n.split(/\s+/).filter(Boolean).map((p) => p[0]).join('').slice(0, 2).toUpperCase() || '?';

export function timeShort(iso: string): string {
  const d = new Date(iso), now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }).replace(/\s/g, '').toLowerCase();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  if (now.getTime() - d.getTime() < 6 * 864e5) return d.toLocaleDateString('en-AU', { weekday: 'short' });
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

/** Total unread across all chats, kept live. For the menu badge. */
export function useTeamUnread(venueId: string | null, userId: string | null) {
  const [total, setTotal] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const load = useCallback(async () => {
    if (!venueId || !userId) return;
    const { data, error } = await supabase.rpc('team_unread');
    if (error) return;
    setTotal(((data ?? []) as Array<{ unread: number }>).reduce((a, r) => a + (r.unread || 0), 0));
  }, [venueId, userId]);
  useEffect(() => {
    if (!venueId || !userId) return;
    void load();
    const soon = () => { if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => void load(), 600); };
    const ch = supabase.channel(`team-unread-${venueId}-${userId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'team_messages', filter: `venue_id=eq.${venueId}` }, soon)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'team_channel_members', filter: `user_id=eq.${userId}` }, soon)
      .subscribe();
    window.addEventListener('opsalo:team-read', soon);
    return () => { if (timer.current) clearTimeout(timer.current); supabase.removeChannel(ch); window.removeEventListener('opsalo:team-read', soon); };
  }, [venueId, userId, load]);
  return total;
}

/** Signed links for chat photos, cached for the session. */
const urlCache = new Map<string, { url: string; at: number }>();
export async function photoUrls(paths: string[]): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  const need: string[] = [];
  for (const p of paths) { const c = urlCache.get(p); if (c && Date.now() - c.at < 50 * 60_000) out[p] = c.url; else need.push(p); }
  if (need.length) {
    const { data } = await supabase.storage.from('team-chat').createSignedUrls(need, 3600);
    for (const r of data ?? []) if (r.signedUrl && r.path) { urlCache.set(r.path, { url: r.signedUrl, at: Date.now() }); out[r.path] = r.signedUrl; }
  }
  return out;
}

/** Where popups go (inside #root in the iOS app so they get taps). */
export const modalRoot = () => (document.documentElement.classList.contains('cap-native') ? document.getElementById('root') ?? document.body : document.body);
